/**
 * BillingTimingNotice - Mid-cycle billing UX clarity component
 * 
 * Displays a clear message explaining that features/bundles:
 * - Activate immediately upon purchase
 * - Billing begins on the tenant's next billing date
 * - User effectively enjoys the feature free until then
 * 
 * This follows FSP anchor-date billing rules (no proration).
 */

import { CalendarBlank, CheckCircle, Sparkle } from "phosphor-react";

/**
 * Format date for display
 * @param {string} dateStr - ISO date string
 * @returns {string} Formatted date like "March 20, 2026"
 */
function formatBillingDate(dateStr) {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

/**
 * BillingTimingNotice Component
 * 
 * @param {Object} props
 * @param {string} props.nextBillingDate - ISO string for the next billing date
 * @param {string} props.variant - "default" | "compact" (default: "default")
 * @param {string} props.className - Additional CSS classes
 */
export function BillingTimingNotice({ 
  nextBillingDate, 
  variant = "default",
  className = "" 
}) {
  const formattedDate = formatBillingDate(nextBillingDate);
  
  // Don't render if no date available
  if (!formattedDate) return null;
  
  if (variant === "compact") {
    return (
      <div className={`flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 ${className}`}>
        <Sparkle size={16} weight="fill" className="text-emerald-500 flex-shrink-0" />
        <span>
          <strong>Activates now</strong> — billing begins {formattedDate}
        </span>
      </div>
    );
  }
  
  // Default (full) variant
  return (
    <div className={`bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-sm p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <CheckCircle size={24} weight="fill" className="text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-emerald-900 flex items-center gap-2">
            Activates Immediately
            <Sparkle size={14} weight="fill" className="text-emerald-500" />
          </h4>
          <p className="text-sm text-emerald-700 mt-1">
            Your new feature will be available right away.
          </p>
          <div className="flex items-center gap-2 mt-2 text-sm">
            <CalendarBlank size={16} className="text-emerald-600" />
            <span className="text-emerald-800">
              Billing begins <strong>{formattedDate}</strong>
            </span>
          </div>
          <p className="text-xs text-emerald-600 mt-2 italic">
            Enjoy the feature free until then!
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * BillingTimingBadge - Inline badge version for tight spaces
 * 
 * @param {Object} props
 * @param {string} props.nextBillingDate - ISO string for the next billing date
 */
export function BillingTimingBadge({ nextBillingDate }) {
  const formattedDate = formatBillingDate(nextBillingDate);
  
  if (!formattedDate) return null;
  
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-700 bg-emerald-100 rounded-sm">
      <Sparkle size={12} weight="fill" />
      Free until {formattedDate}
    </span>
  );
}

export default BillingTimingNotice;
